/**
 * TAB COORDINATION
 * 
 * Manages which browser-tab is the "prime" tab. This is important for:
 * - ensurign only one tab sends a presence state
 * - ensurign only one tab handles audio recording
 * - allowing a tab to show different UX when many tabs are open
 * 
 * Upon focusing a window, it can become the prime. Then, when the prime
 * tab closes, another tab automatically takes over, if/when it has focus.
 * 
 * Exports:
 * - tabId: the id of the current tab
 * - isLoading: whether the tab-state is still figuring itself out
 * - hasPrimaryTab: whether any tab has been designated as the prime
 * - isPrimaryTab: whether the current tab is the prime
 * - isFocusedTab: whether the current tab has focus
 * 
 */

import { useCallback, useEffect, useMemo, useState } from 'react';

function getOrCreateTabId() {
	// NOTE: Without this, ID's don't survive HMR.
	let tabId = sessionStorage.getItem('meeting-tab-coordination-tab');
	if (!tabId) {
		const newTabId = `tab-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
		sessionStorage.setItem('meeting-tab-coordination-tab', newTabId);
		tabId = newTabId;
	}
	return tabId;
}

const CURRENT_TAB_ID = getOrCreateTabId();
const USED_MEETINGS = new Set<string>();
const TAB_HEARTBEAT_INTERVAL = 250; // 4x per second, since this is all local data
const TAB_TIMEOUT_MS = 5000; // 5 seconds (20 lost heartbeats) to consider a tab dead

interface TabCoordinationState {
	tabId: string;
	timestamp: number;
	meetingId: string;
}

function checkIsFocusedTab() {
	return document.hasFocus() && !document.hidden;
}

function getSharedMeetingState(meetingId: string) {
	const storageKey = `meeting-tab-coordination-meeting:${meetingId}`;
	const primeTabInfo = localStorage.getItem(storageKey);
	return primeTabInfo ? JSON.parse(primeTabInfo) : null;
}

function setSharedMeetingState(meetingId: string, state: TabCoordinationState) {
	const storageKey = `meeting-tab-coordination-meeting:${meetingId}`;
	localStorage.setItem(storageKey, JSON.stringify(state));
	USED_MEETINGS.add(meetingId);
}

// FIX: if I reload, my previous session isn't connected to me
// in any way, therefore, better to actually evict myself from
// the localStorage rather than rely on expiration of the time
// NOTE: this is a global listener and is applied only once
window.addEventListener('beforeunload', () => {
	try {
		const meetings = Array.from(USED_MEETINGS);
		meetings.forEach((meetingId) => {
			const storageKey = `meeting-tab-coordination-meeting:${meetingId}`;
			const primeTabInfo = getSharedMeetingState(meetingId);
			if (primeTabInfo && primeTabInfo.tabId === CURRENT_TAB_ID) {
				localStorage.removeItem(storageKey);
			}
		});
	}
	catch (error) {
		console.error('error evicting primary tab', error);
	}
});

export const useTabCoordination = (meetingId: string) => {
	const [isLoading, setIsLoading] = useState(true);
	const [hasPrimaryTab, setHasPrimaryTab] = useState(false);
	const [isPrimaryTab, setIsPrimaryTab] = useState(false);
	const storageKey = useMemo(() => `meeting-tab-coordination-meeting:${meetingId}`, [meetingId]);
	const [isFocusedTab, setIsFocusedTab] = useState(false);

	const setPrimeTabState = useCallback((state: TabCoordinationState) => {
		setSharedMeetingState(meetingId, state);
	}, [meetingId]);

	const getPrimeTabState = useCallback((): TabCoordinationState | null => {
		return getSharedMeetingState(meetingId);
	}, [meetingId]);

	const createHeartbeat = useCallback(() => {
		return {
			tabId: CURRENT_TAB_ID,
			timestamp: Date.now(),
			meetingId: meetingId || '',
		}
	}, [meetingId]);

	const syncTab = useCallback(() => {
		const now = Date.now();
		const primeTabState = getPrimeTabState();
		const storageIsPrimaryTab = primeTabState && primeTabState.tabId === CURRENT_TAB_ID;
		const storeageIsStale = primeTabState && now - primeTabState.timestamp > TAB_TIMEOUT_MS;

		// cases where this should become the prime tab
		// 1) we are the prime tab (already
		// 2) focus + no prime tab exists
		// 3) focus + the prime tab is stale (unresponsive)
		const alreadyPrimeTab = storageIsPrimaryTab;
		const shouldPrimeTab = checkIsFocusedTab() && (!primeTabState || storeageIsStale);

		if (alreadyPrimeTab || shouldPrimeTab) {
			setIsPrimaryTab(true);
			setPrimeTabState(createHeartbeat());
		}
		else {
			setIsPrimaryTab(false);
		}

		const updatedState = getPrimeTabState();
		setHasPrimaryTab(!!updatedState && updatedState.timestamp > now - TAB_TIMEOUT_MS);
		setIsLoading(false);
	}, [getPrimeTabState, setPrimeTabState, createHeartbeat]);

	// NOTE: effect management
	useEffect(() => {
		function syncFocus() {
			setIsFocusedTab( checkIsFocusedTab() );
		}

		function handleStorageChange(e: StorageEvent) {
			if (e.key === storageKey) {
				syncTab();
				syncFocus();
			}
		}

		const heartbeat = setInterval(() => {
			syncTab();
			syncFocus();
		}, TAB_HEARTBEAT_INTERVAL);

		window.addEventListener('focus', syncFocus);
		window.addEventListener('blur', syncFocus);
		window.addEventListener('storage', handleStorageChange);
		
		syncTab();
		syncFocus();
		
		return () => {
			clearInterval(heartbeat);

			window.removeEventListener('focus', syncFocus);
			window.removeEventListener('blur', syncFocus);
			window.removeEventListener('storage', handleStorageChange);

			// NOTE: we don't run the eviction here, because the hook
			// is meant to function more like a singleton.
			syncFocus();
			syncTab();
		};
	}, [setIsFocusedTab, syncTab, meetingId, storageKey]);

	return useMemo(() => ({
		tabId: CURRENT_TAB_ID,
		isLoading,
		hasPrimaryTab,
		isPrimaryTab,
		isFocusedTab,
	}), [
		isLoading,
		hasPrimaryTab,
		isPrimaryTab,
		isFocusedTab,
	]);
};
